import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { Notification, UserData } from '../types';
import { BellIcon, CommentIcon, BumpIcon, AtIcon, UsersIcon, ChevronLeftIcon, ChevronRightIcon } from './Icons';
import { stickerTextStyle } from './StickerText';
import { getAppLogo } from '../utils/flavor';

const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = Math.max(0, now.getTime() - date.getTime());
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 10) return "Just now!";
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;

    return date.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const NotificationMediaCarousel = ({ media }: { media: any[] }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const handlePrev = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentIndex(prev => prev === 0 ? media.length - 1 : prev - 1);
    };

    const handleNext = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentIndex(prev => prev === media.length - 1 ? 0 : prev + 1);
    };

    if (!media || media.length === 0) return null;

    const currentMedia = media[currentIndex];

    return (
        <div className="mt-3 relative rounded-2xl overflow-hidden border border-white/5 bg-black/20 group">
            {currentMedia.type === 'video' ? (
                <video src={`${currentMedia.url}#t=0.001`} className="w-full max-h-[40vh] object-contain bg-black" controls playsInline />
            ) : currentMedia.type === 'audio' ? (
                <audio src={currentMedia.url} className="w-full" controls />
            ) : (
                <img src={currentMedia.url} className="w-full max-h-[40vh] object-contain bg-black" />
            )}

            {media.length > 1 && (
                <>
                    <div className="absolute inset-y-0 left-0 flex items-center pl-2 z-10">
                        <button onClick={handlePrev} className="p-2 rounded-full bg-black/50 text-white hover:bg-black/80 backdrop-blur-md">
                            <ChevronLeftIcon className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 z-10">
                        <button onClick={handleNext} className="p-2 rounded-full bg-black/50 text-white hover:bg-black/80 backdrop-blur-md">
                            <ChevronRightIcon className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5 z-20">
                        {media.map((_, i) => (
                            <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === currentIndex ? 'w-4 bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]' : 'w-1.5 bg-white/50 hover:bg-white/70'}`} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

interface LiveNotificationPopupProps {
    notification: Notification;
    usersData: Record<string, UserData>;
    communities?: any[];
    currentUser?: string | null;
    onClose: () => void;
    onNavigateToPost?: (postId: string) => void;
    onNavigateToProfile?: (userId: string) => void;
    onNavigateToFootprint?: () => void;
    onNavigateToChat?: (username: string) => void;
    onMarkAsRead?: (notificationId: number) => void;
}

export const LiveNotificationPopup: React.FC<LiveNotificationPopupProps> = ({
    notification,
    usersData,
    communities = [],
    currentUser = null,
    onClose,
    onNavigateToPost,
    onNavigateToProfile,
    onNavigateToFootprint,
    onNavigateToChat,
    onMarkAsRead
}) => {
    const [isVisible, setIsVisible] = useState(true);
    const x = useMotionValue(0);
    const opacity = useTransform(x, [-100, 0, 100], [0, 1, 0]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(false);
            setTimeout(onClose, 500); // Allow exit animation
        }, 6000); // Slightly longer to allow reading media carousel or follow buttons if they exist

        return () => clearTimeout(timer);
    }, [onClose]);

    const handleDragEnd = (_: any, info: any) => {
        if (Math.abs(info.offset.x) > 100) {
            setIsVisible(false);
            setTimeout(onClose, 200);
        }
    };

    const handleClick = () => {
        if (notification.type === 'system') {
            // Do nothing on container click
        } else if (notification.type === 'footprint') {
            onNavigateToFootprint?.();
        } else if (notification.type === 'link') {
            onNavigateToChat?.(notification.fromUser);
        } else if (notification.postId) {
            onNavigateToPost?.(notification.postId.toString());
        } else if (notification.type === 'community' && notification.communityId) {
            onNavigateToProfile?.(notification.fromUser);
        } else {
            onNavigateToProfile?.(notification.fromUser);
        }
        if (!notification.read) onMarkAsRead?.(notification.id);
        setIsVisible(false);
        setTimeout(onClose, 200);
    };

    const fromUser = notification.fromUser;

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ y: -100, opacity: 0, scale: 0.9 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: -100, opacity: 0, scale: 0.9 }}
                    style={{ x, opacity }}
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    onDragEnd={handleDragEnd}
                    onClick={handleClick}
                    onPointerDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                    className="fixed top-[max(1rem,var(--safe-area-top))] left-4 right-4 z-[2000] cursor-pointer"
                >
                    <div 
                        className="bg-blue-600/10 backdrop-blur-2xl border border-blue-500/30 p-4 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.5),0_0_30px_rgba(37,99,235,0.2)] flex items-start gap-4 active:scale-[0.98] transition-transform"
                    >
                        <div 
                            className="relative cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (notification.fromUser && notification.fromUser !== 'system' && notification.fromUser !== 'goodlyfe') {
                                    onClose();
                                    onNavigateToProfile?.(notification.fromUser);
                                } else if (notification.type === 'system' || notification.fromUser === 'goodlyfe' || notification.fromUser === 'system') {
                                    onClose();
                                    onNavigateToProfile?.('goodlyfe');
                                }
                            }}
                        >
                            <img
                                src={
                                    notification.type === 'footprint'
                                        ? '/assets/footprint_logo.png'
                                        : notification.type === 'system'
                                            ? '/assets/goodieboy.png'
                                            : (notification.type === 'community' && notification.communityId)
                                                ? (communities.find(c => c._id === notification.communityId)?.avatar || getAppLogo())
                                                : (usersData[notification.fromUser]?.pfp || ((notification.fromUser === 'goodlyfe' || notification.fromUser === '@goodlyfe' || notification.fromUser === 'system' || notification.fromUser === '@system') ? '/assets/goodieboy.png' : getAppLogo()))
                                }
                                className="w-12 h-12 rounded-full object-cover"
                                alt={fromUser}
                            />
                            <div className="absolute -bottom-1 -right-1 bg-blue-600 rounded-full p-1 border-2 border-gray-900">
                                {notification.type === 'like' && <span className="text-[10px]">❤️</span>}
                                {notification.type === 'comment' && <CommentIcon className="w-3 h-3 text-white" />}
                                {notification.type === 'reply' && <CommentIcon className="w-3 h-3 text-white" />}
                                {notification.type === 'bump' && <BumpIcon className="w-4 h-4 text-emerald-400" />}
                                {notification.type === 'mention' && <AtIcon className="w-3 h-3 text-white" />}
                                {notification.type === 'follow' && <UsersIcon className="w-3 h-3 text-white" />}
                                {(notification.type === 'friend' || notification.type === 'friend_request' || notification.type === 'friend_accepted') && <UsersIcon className="w-3 h-3 text-green-400" />}
                                {notification.type === 'community' && <UsersIcon className="w-3 h-3 text-white" />}
                                {notification.type === 'footprint' && <span className="text-[10px]">👣</span>}
                                {notification.type === 'link' && <span className="text-[10px]">📍</span>}
                                {notification.type === 'system' && <BellIcon className="w-3 h-3 text-white" />}
                            </div>
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                                <p className="text-sm font-bold leading-tight" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>
                                    {notification.type === 'footprint'
                                        ? 'Footprint'
                                        : (notification.type === 'system' || notification.fromUser === 'goodlyfe' || notification.fromUser === 'system')
                                            ? 'GoodieBoy'
                                            : (usersData[notification.fromUser]?.displayName || notification.fromUser)
                                    }
                                </p>
                            </div>
                            <div className="mt-1">
                                <span className="text-sm font-normal" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>
                                    {notification.type === 'like' && 'liked your post'}
                                    {notification.type === 'comment' && 'commented on your post'}
                                    {notification.type === 'reply' && 'replied to your comment'}
                                    {notification.type === 'friend' && 'became friends with you'}
                                    {notification.type === 'friend_request' && 'sent you a friend request'}
                                    {notification.type === 'friend_accepted' && 'accepted your friend request'}
                                    {notification.type === 'follow' && 'started following you'}
                                    {notification.type === 'bump' && 'bumped your post'}
                                    {notification.type === 'vouch' && 'vouched your footprint. +1 Lyfe pt!'}
                                    {notification.type === 'mention' && (notification.isDMMention ? '' : 'mentioned you')}
                                    {notification.type === 'community' && (notification.postId ? 'posted in a community' : 'added you to a community')}
                                    {(notification.type === 'follow_request' || notification.type === 'friend_request') && 'sent you a friend request'}
                                    {(notification.type === 'follow_accepted' || notification.type === 'friend_accepted') && 'accepted your friend request'}
                                    {notification.type === 'thread_comment' && 'commented on a post you commented on'}
                                    {notification.type === 'footprint' && notification.text}
                                    {notification.type === 'link' && (() => {
                                        let isLoc = false;
                                        try {
                                            const payload = JSON.parse(notification.message || '{}');
                                            isLoc = !!payload.isLocation;
                                        } catch (e) {}
                                        return notification.text ? `wants to link: "${notification.text}"` : (isLoc ? "sent you their location!" : "wants to link up!");
                                    })()}
                                </span>
                                {((notification.type === 'system' && notification.message) || (notification.type === 'community' && notification.message) || notification.type === 'mention' || notification.type === 'comment' || notification.type === 'reply' || notification.type === 'thread_comment' || (notification.type === 'vouch' && notification.vouchMessage)) && (
                                    <p className="text-sm font-medium mt-2 leading-relaxed break-words whitespace-pre-wrap" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>
                                        {notification.type === 'system' 
                                            ? notification.message
                                            : notification.type === 'community'
                                                ? notification.message
                                                : notification.type === 'vouch'
                                                    ? notification.vouchMessage
                                                    : (notification.type === 'comment' || notification.type === 'reply' || notification.type === 'thread_comment')
                                                        ? (notification.commentText || notification.text)
                                                        : (notification.isDMMention ? "wow, you're pretty popular" : (notification.text || 'Mentioned you in a post'))
                                        }
                                    </p>
                                )}
                            </div>
                            {notification.type === 'system' && notification.media && notification.media.length > 0 && (
                                <NotificationMediaCarousel media={notification.media} />
                            )}
                            {notification.type === 'link' && (() => {
                                let linkImgUrl = notification.imageUrl;
                                try {
                                    const payload = JSON.parse(notification.message || '{}');
                                    if (payload.imageUrl) linkImgUrl = payload.imageUrl;
                                } catch (e) {}
                                return linkImgUrl ? (
                                    <NotificationMediaCarousel media={[{ type: 'image', url: linkImgUrl }]} />
                                ) : null;
                            })()}
                            <p className="text-[10px] mt-1 uppercase font-bold" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>{formatRelativeTime(notification.timestamp)}</p>
                            
                            {notification.type === 'follow_request' && (
                                <div className="flex gap-2 mt-3">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.dispatchEvent(new CustomEvent('app.acceptFollowRequest', {
                                                detail: { targetUser: currentUser, requester: notification.fromUser }
                                            }));
                                            setIsVisible(false);
                                            setTimeout(onClose, 200);
                                        }}
                                        className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-black uppercase tracking-widest rounded-full transition-all active:scale-95"
                                    >
                                        Accept
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.dispatchEvent(new CustomEvent('app.rejectFollowRequest', {
                                                detail: { targetUser: currentUser, requester: notification.fromUser }
                                            }));
                                            setIsVisible(false);
                                            setTimeout(onClose, 200);
                                        }}
                                        className="px-4 py-1.5 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white text-[10px] font-black uppercase tracking-widest rounded-full transition-all active:scale-95 border border-white/10"
                                    >
                                        Reject
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
